import { PrismicRichText } from '@prismicio/react';
import IconCArrowRight from '@remote-com/norma/icons/arrow-right.svg';
import Image from 'next/image';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { Title, Section as SectionWrapper, List as ListWrapper } from './AboutStudySlice';
import { Description } from './BenefitsSlice';

import { transformProperties, fieldHasContent } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { SimpleFormModalButton } from '@/src/views/Referrals/SimpleForm';

export const Section = styled(SectionWrapper)`
  text-align: left;
  background: var(--colors-blank);

  ${fromTabletBreakpoint(css`
    clip-path: ellipse(120% 100% at 50% 0%);
  `)}
`;

export const SecondTitle = styled(Title)`
  && {
    ${({ theme }) => theme.typography.titleSM};
    font-size: 16px;
    margin-top: 32px;
  }
`;

export const List = styled(ListWrapper)`
  li {
    &::after {
      left: -41px;
    }
  }
`;

export const SecondList = styled(List)`
  margin: 0;
  ul,
  ol {
    display: block;

    li {
      ${fromTabletBreakpoint(css`
        max-width: 400px;
      `)}
    }
  }

  li {
    &::after {
      -webkit-mask-image: url(${staticAssetUrl('/images/workforce-report/circle-arrow-right.svg')});
      mask-image: url(${staticAssetUrl('/images/workforce-report/circle-arrow-right.svg')});
    }
  }
`;

const CtaBlock = styled.div`
  background: #fef7f9;
  border-radius: 8px;
  padding: 24px 32px;
  display: flex;
  max-width: 420px;
  justify-content: space-between;
  align-items: center;

  ${tabletBreakpoint(css`
    margin-top: 30px;
  `)}
`;

const CtaBlockContent = styled.div`
  width: 45%;
`;

const BottomList = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  ${tabletBreakpoint(css`
    flex-wrap: wrap;
  `)}
`;

const CtaBlockTitle = styled.div`
  ${({ theme }) => theme.typography.titleSM};
  font-family: var(--typography-fontBossa);
`;

export const CTAButton = styled(Button).attrs({
  IconAfter: IconCArrowRight,
})`
  && {
    background-color: var(--secondaryColor);
    color: var(--colors-blank);
    ${({ theme }) => theme.typography.body};
    margin-top: 14px;

    svg {
      margin-left: 20px;
    }
  }
`;

const AdditionalLayer = styled.div`
  ${fluidProp('padding-top', '100px', '200px')};
  background: #fff5f5;
  margin-top: -140px;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

export function WhatYouGetSlice({ slice }) {
  const { primary, context } = transformProperties(slice);
  const {
    title,
    description,
    icon,
    secondListTitle,
    secondList,
    ctaBlockTitle,
    ctaBlockDescription,
    ctaBlockLabel,
    ctaBlockLink,
    ctaBlockIllustration,
  } = primary;

  const {
    formId,
    gatedFile,
    modalFormTitle,
    modalFormDescription,
    modalFormSubmitLabel,
    downloadButtonLabel,
    eventName,
  } = context;

  return (
    <>
      <Section>
        <ContentContainer>
          {icon && (
            <Image
              src={icon?.url}
              alt={icon?.alt || ''}
              width={icon?.dimensions.width}
              height={icon?.dimensions.height}
              quality="100"
            />
          )}
          {fieldHasContent(title) && (
            <Title>
              <PrismicRichText field={title} />
            </Title>
          )}
          {fieldHasContent(description) && (
            <List>
              <PrismicRichText field={description} />
            </List>
          )}
          {fieldHasContent(secondListTitle) && (
            <SecondTitle>
              <PrismicRichText field={secondListTitle} />
            </SecondTitle>
          )}
          <BottomList>
            {fieldHasContent(secondList) && (
              <SecondList>
                <PrismicRichText field={secondList} />
              </SecondList>
            )}
            <CtaBlock>
              <CtaBlockContent>
                {fieldHasContent(ctaBlockTitle) && (
                  <CtaBlockTitle>
                    <PrismicRichText field={ctaBlockTitle} />
                  </CtaBlockTitle>
                )}
                {fieldHasContent(ctaBlockDescription) && (
                  <Description>
                    <PrismicRichText field={ctaBlockDescription} />
                  </Description>
                )}
                {ctaBlockLabel &&
                  (ctaBlockLink ? (
                    <CTAButton href={ctaBlockLink?.url} target={ctaBlockLink?.target}>
                      {ctaBlockLabel}
                    </CTAButton>
                  ) : (
                    <SimpleFormModalButton
                      modalFormId={formId}
                      modalFormSubmitLabel={modalFormSubmitLabel}
                      modalFormDescription={modalFormDescription}
                      modalFormTitle={modalFormTitle}
                      gatedFile={gatedFile}
                      gatedFileDownloadLabel={downloadButtonLabel}
                      eventName={eventName}
                      button={{ as: CTAButton }}
                    >
                      {ctaBlockLabel}
                    </SimpleFormModalButton>
                  ))}
              </CtaBlockContent>
              {ctaBlockIllustration && (
                <Image
                  src={ctaBlockIllustration?.url}
                  alt={ctaBlockIllustration?.alt || ''}
                  width={ctaBlockIllustration?.dimensions.width}
                  height={ctaBlockIllustration?.dimensions.height}
                  quality="100"
                />
              )}
            </CtaBlock>
          </BottomList>
        </ContentContainer>
      </Section>
      <AdditionalLayer />
    </>
  );
}
